import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ClockIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const StockAlertsTab = ({ openStockModal }) => {
  const navigate = useNavigate();
  const { 
    products, 
    stockItems,
    stockAlerts,
    inventoryItems,
    fetchAllData,
    addNotification,
    getStockAlerts,
    refreshInventoryData
  } = useAppData();
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [alertFilter, setAlertFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [alertsData, setAlertsData] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchAllData();
        // Refresh inventory data to get latest alerts
        await refreshInventoryData();
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };
    
    loadData();
    
    // Refresh data every minute to keep alerts current
    const refreshInterval = setInterval(() => {
      loadData();
    }, 60000);
    
    // Clean up on unmount
    return () => clearInterval(refreshInterval);
  }, [fetchAllData, refreshInventoryData]);

  // Generate real-time alerts from product data
  useEffect(() => {
    // Create function to process both API alerts and generate additional alerts from product data
    const processRealTimeAlerts = () => {
      const allAlerts = [];
      let alertIndex = 1;
      
      // Process products for low stock alerts
      if (products && products.length > 0) {
        products.forEach(product => {
          const stock = parseInt(product.stock) || 0;
          const minStock = parseInt(product.minStockLevel) || 5;
          
          // Low stock alert
          if (stock <= minStock) {
            const severity = stock === 0 ? 'high' : (stock <= minStock / 2 ? 'medium' : 'low');
            allAlerts.push({
              id: `ALRT-${String(alertIndex++).padStart(3, '0')}`,
              date: new Date().toISOString().split('T')[0],
              type: 'low_stock',
              productId: product.id,
              product: product.name,
              details: `Stock actuel: ${stock} (Min: ${minStock})`,
              status: 'pending',
              severity: severity
            });
          }
          
          // Check for inactive products (no movements in the last 90 days)
          if (product.lastMovementDate) {
            const lastMovement = new Date(product.lastMovementDate);
            const today = new Date();
            const daysSinceLastMovement = Math.floor((today - lastMovement) / (1000 * 60 * 60 * 24));
            
            if (daysSinceLastMovement > 90) {
              allAlerts.push({
                id: `ALRT-${String(alertIndex++).padStart(3, '0')}`,
                date: new Date().toISOString().split('T')[0],
                type: 'inactive',
                productId: product.id,
                product: product.name,
                details: `Inactif depuis ${daysSinceLastMovement} jours`,
                status: 'pending',
                severity: 'low'
              });
            }
          }
          
          // Check for overstock
          const optimalStock = parseInt(product.optimalStockLevel) || (minStock * 2);
          if (stock > optimalStock * 1.5) {
            allAlerts.push({
              id: `ALRT-${String(alertIndex++).padStart(3, '0')}`,
              date: new Date().toISOString().split('T')[0],
              type: 'overstock',
              productId: product.id,
              product: product.name,
              details: `Stock: ${stock} (Optimal: ${optimalStock})`,
              status: 'pending',
              severity: 'low'
            });
          }
        });
      }
      
      // Add any API alerts
      if (stockAlerts && stockAlerts.length > 0) {
        stockAlerts.forEach((alert) => {
          // Only add if we don't already have an alert for this product/type combination
          const existingAlertIndex = allAlerts.findIndex(
            a => a.productId === alert.id && a.type === 'low_stock'
          );
          
          if (existingAlertIndex === -1) {
            allAlerts.push({
              id: `ALRT-${String(alertIndex++).padStart(3, '0')}`,
        date: new Date().toISOString().split('T')[0],
        type: 'low_stock',
        productId: alert.id,
              product: alert.productName || "Produit inconnu",
              details: alert.message || "Stock faible",
              status: 'pending',
        severity: alert.severity === 'critical' ? 'high' : 'medium'
            });
          }
        });
      }
      
      console.log(`Generated ${allAlerts.length} real-time alerts`);
      setAlertsData(allAlerts);
    };
    
    // Process alerts when products or stockAlerts change
    processRealTimeAlerts();
    
  }, [products, stockAlerts]);

  // Filter alerts based on search, type and status
  const filteredAlerts = useMemo(() => {
    return alertsData.filter(alert => {
      // Match search term
      const matchesSearch = 
        !searchTerm || 
        alert.product.toLowerCase().includes(searchTerm.toLowerCase()) || 
        alert.id.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Match alert type
      const matchesType = alertFilter === 'all' || alert.type === alertFilter;
      
      // Match status
      const matchesStatus = statusFilter === 'all' || alert.status === statusFilter;
      
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [alertsData, searchTerm, alertFilter, statusFilter]);

  // Handle marking an alert as resolved
  const handleResolveAlert = (alert) => {
    setAlertsData(prev => 
      prev.map(a => 
        a.id === alert.id 
          ? { ...a, status: a.status === 'pending' ? 'resolved' : 'pending' } 
          : a
      )
    );
    
    // Create notification for tracking
    addNotification({
      id: `NOTIF-${Date.now()}`,
      title: `Alerte ${alert.status === 'pending' ? 'résolue' : 'rouverte'}`,
      message: `Alerte ${alert.id} pour ${alert.product} a été ${alert.status === 'pending' ? 'marquée comme résolue' : 'rouverte'}`,
      type: alert.status === 'pending' ? 'success' : 'warning',
      timestamp: new Date().toISOString(),
      read: false
    });
    
    toast.success(alert.status === 'pending' 
      ? `Alerte marquée comme traitée` 
      : `Alerte marquée comme en attente`
    );
  };

  // Handle resolving all alerts
  const handleResolveAll = () => {
    setAlertsData(prev => prev.map(a => ({ ...a, status: 'resolved' })));
    toast.success('Toutes les alertes ont été marquées comme traitées');
  };
  
  // Handle opening the stock adjustment modal for a product
  const handleAdjustStock = (productId) => {
    if (openStockModal) {
      openStockModal(productId);
    } else {
      navigate(`/products/${productId}`);
    }
  };
  
  // Handle creating transfer for product with low stock
  const handleCreateTransfer = (productId) => {
    // Store the product ID in session storage to pre-select it in transfer form
    sessionStorage.setItem('transferProductId', productId);
    navigate('/stock/transfer');
  };

  // Get alert type label
  const getAlertTypeLabel = (type) => {
    const types = {
      'low_stock': 'Stock faible',
      'expiration': 'Expiration',
      'inactive': 'Produit inactif',
      'overstock': 'Surstock'
    };
    return types[type] || type;
  };
  
  // Get alert type styles
  const getAlertTypeStyles = (type) => {
    const styles = {
      'low_stock': 'bg-red-500/20 text-red-400',
      'expiration': 'bg-yellow-500/20 text-yellow-400',
      'inactive': 'bg-purple-500/20 text-purple-400',
      'overstock': 'bg-blue-500/20 text-blue-400'
    };
    return styles[type] || 'bg-gray-500/20 text-gray-400';
  };
  
  // Get severity indicator styles
  const getSeverityStyles = (severity) => {
    const bgStyles = {
      'high': 'bg-red-500/20',
      'medium': 'bg-yellow-500/20',
      'low': 'bg-blue-500/20'
    };
    const dotStyles = {
      'high': 'bg-red-500',
      'medium': 'bg-yellow-500',
      'low': 'bg-blue-500'
    };
    return { bg: bgStyles[severity], dot: dotStyles[severity] };
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Alertes de stock</h2>
          <p className="text-white/60 mt-1">
            Gérez les alertes de stock faible et autres problèmes liés à l'inventaire
          </p>
        </div>
        <div>
          <button 
            onClick={handleResolveAll}
            className="px-4 py-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            Tout marquer comme traité
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="relative lg:col-span-2">
          <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
          <input
            type="text"
            placeholder="Rechercher une alerte..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="relative">
          <select
            value={alertFilter}
            onChange={(e) => setAlertFilter(e.target.value)}
            className="w-full pl-4 pr-10 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
          >
            <option value="all">Tous les types</option>
            <option value="low_stock">Stock faible</option>
            <option value="expiration">Expiration</option>
            <option value="inactive">Inactif</option>
            <option value="overstock">Surstock</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-white/50">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full pl-4 pr-10 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="resolved">Résolues</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-white/50">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Alert filters pills */}
      <div className="flex flex-wrap gap-3">
        <button 
          className={`px-4 py-2 rounded-full text-sm font-medium ${alertFilter === 'all' && statusFilter === 'all' ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
          onClick={() => { setAlertFilter('all'); setStatusFilter('all'); }}
        >
          Toutes ({alertsData.length})
        </button>
        <button 
          className={`px-4 py-2 rounded-full text-sm font-medium ${statusFilter === 'pending' && alertFilter === 'all' ? 'bg-yellow-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
          onClick={() => { setStatusFilter('pending'); setAlertFilter('all'); }}
        >
          En attente ({alertsData.filter(a => a.status === 'pending').length})
        </button>
        <button 
          className={`px-4 py-2 rounded-full text-sm font-medium ${statusFilter === 'resolved' && alertFilter === 'all' ? 'bg-green-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
          onClick={() => { setStatusFilter('resolved'); setAlertFilter('all'); }}
        >
          Traitées ({alertsData.filter(a => a.status === 'resolved').length})
        </button>
        <button 
          className={`px-4 py-2 rounded-full text-sm font-medium ${alertFilter === 'low_stock' ? 'bg-red-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
          onClick={() => { setAlertFilter('low_stock'); setStatusFilter('all'); }}
        >
          Stock faible ({alertsData.filter(a => a.type === 'low_stock').length})
        </button>
        <button 
          className={`px-4 py-2 rounded-full text-sm font-medium ${alertFilter === 'expiration' ? 'bg-yellow-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
          onClick={() => { setAlertFilter('expiration'); setStatusFilter('all'); }}
        >
          Expiration ({alertsData.filter(a => a.type === 'expiration').length})
        </button>
      </div>

      {/* Alerts list */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          {filteredAlerts.length > 0 ? (
            <table className="w-full divide-y divide-white/10">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Priorité</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Détails</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredAlerts.map((alert, index) => {
                  const severityStyles = getSeverityStyles(alert.severity);
                  return (
                    <motion.tr
                      key={alert.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 * index }}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex h-6 w-6 rounded-full ${severityStyles.bg}`}>
                          <span className={`h-4 w-4 m-auto rounded-full ${severityStyles.dot}`}></span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {alert.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getAlertTypeStyles(alert.type)}`}>
                          {getAlertTypeLabel(alert.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        <div className="flex items-center">
                          <div 
                            className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium text-sm mr-2"
                          >
                            {alert.product.charAt(0)}
                          </div>
                          <span>{alert.product}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">
                        {alert.details}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          alert.status === 'pending' 
                            ? 'bg-yellow-500/20 text-yellow-400' 
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {alert.status === 'pending' ? 'En attente' : 'Traité'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button 
                            onClick={() => navigate(`/products/${alert.productId}`)}
                            className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors" 
                            title="Voir le produit"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          
                          <button 
                            onClick={() => handleResolveAlert(alert)}
                            className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors" 
                            title={alert.status === 'pending' ? "Marquer comme traité" : "Marquer comme en attente"}
                          >
                            {alert.status === 'pending' ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <ArrowPathIcon className="h-4 w-4" />
                            )}
                          </button>
                          
                          {/* Show adjustment option for low stock and overstock */}
                          {(alert.type === 'low_stock' || alert.type === 'overstock') && (
                            <button 
                              onClick={() => handleAdjustStock(alert.productId)}
                              className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors" 
                              title="Ajuster le stock"
                            >
                              {alert.type === 'low_stock' ? (
                                <PlusIcon className="h-4 w-4" />
                              ) : (
                                <XMarkIcon className="h-4 w-4" />
                              )}
                            </button>
                          )}
                          
                          {/* Show transfer option for low stock */}
                          {alert.type === 'low_stock' && (
                            <button 
                              onClick={() => handleCreateTransfer(alert.productId)}
                              className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors" 
                              title="Créer un transfert"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8z" />
                                <path d="M12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
                              </svg>
                            </button>
                          )}
                          
                          {/* Show clock option for expiration alerts */}
                          {alert.type === 'expiration' && (
                            <button 
                              onClick={() => toast.info("Notification d'expiration programmée")}
                              className="p-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors" 
                              title="Programmer un rappel"
                            >
                              <ClockIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12 text-white/60">
              <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-white/30 mb-4" />
              <p className="text-lg font-medium text-white mb-1">Aucune alerte trouvée</p>
              <p className="text-white/50">
                {searchTerm || alertFilter !== 'all' || statusFilter !== 'all'
                  ? 'Essayez de modifier vos filtres de recherche'
                  : 'Toutes les alertes ont été traitées'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockAlertsTab;